import { useState, useEffect, useContext, useCallback } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { getChatList, markMessageAsRead } from '../../services/chatService';
import { getNotificationGroupByType, markNotificationAsRead } from '@services/notificationService';
import { formatDate } from '@utils/dateHelper';
import NoStylist from '@components/NoStylist';
import { CountContext } from '../../context/CountContext';
import { getAllNotificationCount } from '@services/chatService';
import { useTranslation } from 'react-i18next';
import type { ChatListItem, NotificationGroup } from '@app-types/models/chat';
import { PageContainer, PageHeader, SectionCard, EmptyState, LoadingState } from '../ui';
import { brand } from '../../theme/tokens';

interface ChatData {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  avatar: string;
}

interface NotificationRowData {
  id: string;
  type: string;
  content: string;
  timestamp: string;
  isNew: boolean;
  subject?: string;
  link?: string;
}

const notificationIcon = (type: string) => {
  switch (type) {
    case 'reminder':
    case 'appointment':
      return <CalendarMonthOutlinedIcon />;
    case 'payment':
      return <CreditCardOutlinedIcon />;
    case 'offer':
      return <LocalOfferOutlinedIcon />;
    case 'system':
      return <CampaignOutlinedIcon />;
    default:
      return <NotificationsNoneOutlinedIcon />;
  }
};

interface NotificationRowProps {
  notification: NotificationRowData;
  onClick: () => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onClick }) => (
  <ListItemButton
    onClick={onClick}
    sx={{
      minHeight: 44,
      alignItems: 'flex-start',
      gap: 1.5,
      px: { xs: 2, sm: 2.5 },
      py: 1.5,
      '&:active': { bgcolor: brand.goldSoft },
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 44,
        height: 44,
        borderRadius: 2,
        display: 'grid',
        placeItems: 'center',
        color: 'primary.dark',
        background: brand.goldSoft,
        '& svg': { fontSize: 22 },
      }}
    >
      {notificationIcon(notification.type)}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      {notification.subject && (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: notification.isNew ? 700 : 600, lineHeight: 1.3 }}
          noWrap
        >
          {notification.subject}
        </Typography>
      )}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontWeight: notification.isNew ? 600 : 400,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {notification.content}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {notification.timestamp}
      </Typography>
    </Box>
    {notification.isNew && (
      <Box sx={{ flexShrink: 0, width: 10, height: 10, borderRadius: '50%', bgcolor: brand.gold, mt: 0.75 }} />
    )}
  </ListItemButton>
);

interface ChatItemProps {
  chat: ChatData;
  onClick: () => void;
}

const ChatItem: React.FC<ChatItemProps> = ({ chat, onClick }) => (
  <ListItemButton
    onClick={onClick}
    sx={{
      minHeight: 44,
      gap: 1.5,
      px: { xs: 2, sm: 2.5 },
      py: 1.5,
      '&:active': { bgcolor: brand.goldSoft },
    }}
  >
    <Avatar
      src={chat.avatar}
      alt={chat.name}
      variant="rounded"
      sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 2 }}
    />
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
          {chat.name}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
          {chat.timestamp}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ flexGrow: 1, minWidth: 0, fontWeight: chat.unreadCount > 0 ? 600 : 400 }}
        >
          {chat.lastMessage}
        </Typography>
        {chat.unreadCount > 0 && (
          <Badge
            badgeContent={chat.unreadCount}
            color="primary"
            sx={{ flexShrink: 0, mr: 1.25, '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
          />
        )}
      </Stack>
    </Box>
  </ListItemButton>
);

const ChatList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const countContext = useContext(CountContext);
  const updateCount = countContext?.updateCount;

  const [chatList, setChatList] = useState<ChatListItem[]>([]);
  const [notifications, setNotifications] = useState<NotificationGroup[]>([]);
  const [noStylist, setNoStylist] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize the updateCount function to prevent unnecessary re-renders
  const stableUpdateCount = useCallback(
    (key: string, value: number) => { updateCount?.(key, value); },
    [updateCount]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const list = await getChatList();
        setChatList(Array.isArray(list) ? list : []);
        const notificationList = await getNotificationGroupByType();
        setNotifications(Array.isArray(notificationList) ? notificationList : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        if ((error as { response?: { data: { code: string } } }).response && (error as { response: { data: { code: string } } }).response.data.code === "STAFF_NOT_FOUND") {
          setNoStylist(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [stableUpdateCount]);

  const handleCustomerClick = async (userId: string) => {
    console.log(`User ID to navigate: ${userId}`);
    await markMessageAsRead(userId);
    const count = await getAllNotificationCount();
    stableUpdateCount('totalCount', count.data.totalUnread);
    navigate(`/chat/${userId}`);
  };

  const handleNotificationClick = async (type: string) => {
    console.log(`Notification type to navigate: ${type}`);
    await markNotificationAsRead(type);
    const count = await getAllNotificationCount();
    stableUpdateCount('totalCount', count.data.totalUnread);
    navigate(`/notification/${type}`);
  };

  if (noStylist) {
    return <NoStylist noStylistMessage={t('common.chatListNoStylistMessage')} />;
  }

  const hasNotifications = notifications.length > 0;
  const hasChats = chatList.length > 0;
  const isEmpty = !hasNotifications && !hasChats;

  return (
    <PageContainer>
      <PageHeader
        title={t('chatList.title', 'Messages')}
        subtitle={t('chatList.subtitle', 'Your notifications and conversations in one place.')}
        icon={<ChatBubbleOutlineIcon />}
        classicPath="/classic/chat-list"
      />

      {isLoading ? (
        <LoadingState variant="list" count={5} />
      ) : isEmpty ? (
        <SectionCard>
          <EmptyState
            icon={<ChatBubbleOutlineIcon />}
            title={t('chatList.emptyTitle', 'No messages yet')}
            description={t(
              'chatList.emptyDescription',
              "When customers reach out or you receive updates, they'll show up here."
            )}
          />
        </SectionCard>
      ) : (
        <Stack spacing={2.5}>
          {hasNotifications && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 0.5 }}>
                {t('chatList.notificationsHeader', 'Notifications')}
              </Typography>
              <SectionCard disableContentPadding>
                <List sx={{ p: 0 }} className="notification-list-container">
                  {notifications.map((notification, index) => (
                    <Box key={notification._id}>
                      {index > 0 && <Divider component="li" sx={{ ml: { xs: 8, sm: 8.5 } }} />}
                      <NotificationRow
                        notification={{
                          id: notification._id,
                          type: notification._id,
                          content: notification.lastMessage.content,
                          timestamp: formatDate(notification.lastMessage.createdAt),
                          isNew: !notification.lastMessage.read,
                          subject: notification.lastMessage.subject,
                          link: notification.lastMessage.link,
                        }}
                        onClick={() => handleNotificationClick(notification._id)}
                      />
                    </Box>
                  ))}
                </List>
              </SectionCard>
            </Box>
          )}

          {hasChats && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 0.5 }}>
                {t('chatList.messagesHeader', 'Messages')}
              </Typography>
              <SectionCard disableContentPadding>
                <List sx={{ p: 0 }}>
                  {chatList.map((chat, index) => (
                    <Box key={chat._id}>
                      {index > 0 && <Divider component="li" sx={{ ml: { xs: 8, sm: 8.5 } }} />}
                      <ChatItem
                        chat={{
                          id: chat._id,
                          name: chat.chatUserName,
                          lastMessage: chat.lastMessage.content || 'Sent an image',
                          timestamp: formatDate(chat.lastMessage.createdAt),
                          unreadCount: chat.count,
                          avatar:
                            chat.avatar ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(chat.chatUserName)}&background=random`,
                        }}
                        onClick={() => handleCustomerClick(chat._id)}
                      />
                    </Box>
                  ))}
                </List>
              </SectionCard>
            </Box>
          )}
        </Stack>
      )}
    </PageContainer>
  );
};

export default ChatList;
